/**
 * Reusable staff realtime connection: `GET /api/v1/realtime/events` over fetch (so the Bearer header can
 * be sent; browsers' EventSource cannot), with replay, reconnect, a heartbeat watchdog, token-expiry
 * handling and a clean shutdown. It routes frames to callbacks and knows no feature.
 *
 * Contract (docs/09_API_SPECIFICATION.md section 15):
 * - the server sends `retry: 3000`, then `: ready` once the stream is positioned; hydrate through REST
 *   after `: ready`;
 * - `id` values of one branch rise; after a reconnect send the last one as `Last-Event-ID`;
 * - `stream.resync` has no id: refetch state through REST;
 * - `: hb` heartbeats arrive about every 15 seconds;
 * - the stream closes when the token expires.
 */
import type { ApiClient } from '@/api/client';
import { isApiError } from '@/api/errors';
import { createSseParser } from './sse-parser';
import type { ConnectionState, RealtimeEvent, ResyncEvent } from './types';

export interface RealtimeClientOptions {
  /** The staff API client: it supplies the Bearer token and clears the session on 401. */
  api: ApiClient;
  /** The current staff session's expiry, or null when there is no session. Read before each attempt. */
  getSession: () => { expiresAt: number | null } | null;
  onEvent: (event: RealtimeEvent) => void;
  onResync?: (event: ResyncEvent) => void;
  /** `: ready` received: events from now on will not be missed. */
  onReady?: () => void;
  onStateChange?: (state: ConnectionState) => void;
  path?: string;
  /** Used until the server advertises its own `retry`. */
  defaultRetryMs?: number;
  /** Ceiling for the growing delay after repeated failed attempts. */
  maxRetryMs?: number;
  /** Silence longer than this (about three heartbeats) is treated as a dead connection. */
  heartbeatTimeoutMs?: number;
  now?: () => number;
}

export interface RealtimeClient {
  start(): void;
  stop(): void;
  readonly state: ConnectionState;
  /** Highest event id processed, used as `Last-Event-ID` on reconnect. */
  readonly lastEventId: number | null;
}

function isAbort(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'AbortError';
}

function parseJson(text: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function createRealtimeClient(options: RealtimeClientOptions): RealtimeClient {
  const path = options.path ?? '/realtime/events';
  const now = options.now ?? Date.now;
  const maxRetryMs = options.maxRetryMs ?? 30_000;
  const heartbeatTimeoutMs = options.heartbeatTimeoutMs ?? 45_000;

  let state: ConnectionState = 'idle';
  let lastEventId: number | null = null;
  let retryMs = options.defaultRetryMs ?? 3000;
  let generation = 0;
  let controller: AbortController | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let cancelSleep: (() => void) | undefined;
  let terminal: ConnectionState | null = null;
  let watchdogFired = false;

  function setState(next: ConnectionState): void {
    if (state === next) return;
    state = next;
    options.onStateChange?.(next);
  }

  function clearTimers(): void {
    if (watchdog !== undefined) clearTimeout(watchdog);
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
    watchdog = undefined;
    expiryTimer = undefined;
    cancelSleep?.();
    cancelSleep = undefined;
  }

  function finish(next: ConnectionState, run: number): void {
    if (run !== generation) return;
    terminal = next;
    clearTimers();
    controller?.abort();
    setState(next);
  }

  function armWatchdog(): void {
    if (watchdog !== undefined) clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      watchdogFired = true;
      controller?.abort();
    }, heartbeatTimeoutMs);
  }

  function armExpiry(expiresAt: number | null, run: number): void {
    if (expiresAt === null) return;
    const delay = Math.min(Math.max(expiresAt - now(), 0), 2_147_483_647);
    expiryTimer = setTimeout(() => finish('expired', run), delay);
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      cancelSleep = () => {
        clearTimeout(timer);
        resolve();
      };
    });
  }

  function deliver(message: { event: string; data: string; id?: string }): void {
    const data = parseJson(message.data);
    if (!data) return;

    if (message.event === 'stream.resync') {
      options.onResync?.({ ...data, type: 'stream.resync' } as unknown as ResyncEvent);
      return;
    }

    const fromId = message.id !== undefined ? Number(message.id) : Number(data.event_id);
    const eventId = Number.isFinite(fromId) ? fromId : null;
    // At-least-once delivery: never process an id that is not greater than the last one.
    if (eventId !== null && lastEventId !== null && eventId <= lastEventId) return;
    if (eventId !== null) lastEventId = eventId;

    try {
      options.onEvent({
        ...data,
        type: message.event,
        event_id: eventId ?? (data.event_id as number),
      } as RealtimeEvent);
    } catch (error) {
      // A feature handler's bug must not take the stream down.
      console.error('Realtime event handler failed', error);
    }
  }

  /** Reads one connection until it ends. Returns whether `: ready` was seen. */
  async function readStream(response: Response): Promise<boolean> {
    const body = response.body;
    if (!body) return false;
    let ready = false;
    const parser = createSseParser({
      onMessage: deliver,
      onRetry: (ms) => {
        retryMs = ms;
      },
      onComment: (text) => {
        if (text === 'ready' && !ready) {
          ready = true;
          setState('live');
          options.onReady?.();
        }
      },
    });

    const reader = body.getReader();
    const decoder = new TextDecoder();
    armWatchdog();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        armWatchdog();
        parser.push(decoder.decode(value, { stream: true }));
      }
    } finally {
      if (watchdog !== undefined) clearTimeout(watchdog);
      watchdog = undefined;
      reader.cancel().catch(() => undefined);
    }
    return ready;
  }

  async function run(my: number): Promise<void> {
    let failures = 0;
    let everConnected = false;

    while (my === generation) {
      const session = options.getSession();
      if (!session) return finish('unauthorized', my);
      if (session.expiresAt !== null && now() >= session.expiresAt) return finish('expired', my);

      controller = new AbortController();
      watchdogFired = false;
      armExpiry(session.expiresAt, my);
      setState(everConnected ? 'reconnecting' : 'connecting');

      try {
        const response = await options.api.openStream(path, {
          signal: controller.signal,
          lastEventId: lastEventId !== null ? String(lastEventId) : undefined,
        });
        everConnected = true;
        const ready = await readStream(response);
        // A stream that ended without ever becoming ready counts as a failed attempt.
        failures = ready ? 0 : failures + 1;
      } catch (error) {
        if (my !== generation || terminal) return;
        if (isApiError(error)) {
          if (error.status === 401) return finish('unauthorized', my);
          if (
            error.status >= 400 &&
            error.status < 500 &&
            error.status !== 408 &&
            error.status !== 429
          ) {
            return finish('failed', my);
          }
          failures += 1;
        } else if (isAbort(error)) {
          if (!watchdogFired) return; // stopped or expired
        } else {
          failures += 1;
        }
      }

      if (expiryTimer !== undefined) clearTimeout(expiryTimer);
      expiryTimer = undefined;
      if (my !== generation || terminal) return;

      setState('reconnecting');
      const backoff = failures > 1 ? Math.min(retryMs * 2 ** (failures - 1), maxRetryMs) : retryMs;
      await sleep(backoff);
    }
  }

  return {
    start() {
      if (state === 'connecting' || state === 'live' || state === 'reconnecting') return; // running
      generation += 1;
      terminal = null;
      const my = generation;
      run(my).catch(() => finish('failed', my));
    },

    stop() {
      if (state === 'closed' || state === 'idle') return;
      generation += 1;
      terminal = 'closed';
      clearTimers();
      controller?.abort();
      setState('closed');
    },

    get state() {
      return state;
    },

    get lastEventId() {
      return lastEventId;
    },
  };
}
